import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import mixpanel from 'mixpanel-browser';
import {
  fetchBootstrapEffect,
  signInEffect,
  verifyEmailEffect,
} from 'app/store/Auth/effects';
import {
  acceptSplitEffect,
  createContributionEffect,
  createSplitEffect,
  deleteSongAudioEffect,
  downloadAudioEffect,
  downloadLyricsPdfEffect,
  handleCreateSongWithDeeplinkingEffect,
  inviteCollaboratorsEffect,
  setSongLatestAudioVersionEffect,
  pinSongEffect,
  unpinSongEffect,
  updateSongAudioEffect,
  updateSongEffect,
  uploadAudioEffect,
  createFolderEffect,
  deleteFolderEffect,
} from 'app/store/SongList/effects';
import {
  createSharedLinkEffect,
  createSharedPlaylistLinkEffect,
  revokeSharedPlaylistLinkEffect,
  updateSharedPlaylistLinkEffect,
} from 'app/store/Shares/effects';
import {
  attachAudioToReleaseEffect,
  createReleaseEffect,
  exportReleaseEffect,
  replaceVersionAudioEffect,
  syncVersionEffect,
  updateLabelVersionEffect,
  updateReleaseVersionEffect,
} from 'app/store/Releases/effects';
import {
  addIpiManuallyEffect,
  addIpnManuallyEffect,
  createIsniEffect,
  deleteConnectionEffect,
  updateProfileEffect,
  uploadProfileImageEffect,
} from 'app/store/Profile/effects';
import {
  fetchNotificationsEffect,
  respondToInviteEffect,
} from 'app/store/Notifications/effects';
import { fetchAudioForPlayingEffect } from 'app/store/AudioPlayer/effects';
import { Audio } from 'types/audio';
import {
  lyricsEditAction,
  updateCollaboratorSearchAction,
  updateSongListSearchAction,
  uploadAudioBlockAction,
} from 'app/store/SongList/slice';
import { SongDetailsFormData } from 'app/components/Song/Details/SongDetailsForm/types';
import { SongFormData } from 'app/components/Song/SongForm/types';
import {
  getSubscription,
  getSubscriptionTiersDDL,
} from 'app/store/Subscription/selectors';
import { getStorage } from 'app/store/Storage/selectors';
import {
  bytesToGigabytes,
  getCurrentTierLabel,
} from 'app/components/Storage/utils';
import { LatestSplit } from 'types/splits.types';

import { isProd, objectDeepKeys } from 'app/utils/utils';
import { SongStatusLabel } from 'types/song';
import { config } from 'config';
import { getGlobalSearchByCollaborator } from 'app/store/GlobalSearch/selectors';
import { getSharedPlaylistLinks } from 'app/store/Shares/selectors';
import { getPlaylist } from 'app/store/Playlists/selectors';
import {
  addPlaylistAudiosEffect,
  createPlaylistEffect,
  deletePlaylistAudioEffect,
  uploadPlaylistImageEffect,
} from 'app/store/Playlists/effects';
import { setShowUpgradePopup } from 'app/store/Storage/slice';
import {
  getCurrentChannel,
  getCurrentChannelMessages,
} from 'app/store/Chat/selectors';
import { createChatMessageEffect } from 'app/store/Chat/effects';
import { setSharedAudio } from 'app/store/Shares/slice';
import { getUserGuid } from 'app/utils/helpers';
import { setCoWriterBarSettings } from 'app/store/Settings/slice';
import { fetchLyricsInspirationSearchEffect } from 'app/store/Subscription/effects';
import { TuneCoreVersionDetailsFormData } from 'app/components/Releases/TuneCoreVersionDetailsForm/types';
import {
  getAudioAddedLocation,
  getPlaylistPlayedLocation,
  getPlaylistUpgradePopupEventName,
  getUpdatedIdsUserProperty,
  isSplitAcceptedByAll,
  removePersonalDataFromUrl,
  shouldSendDefaultIdEvent,
} from './utils';
import { RespondToInviteEffectData } from '../../Notifications/effects.types';
import {
  audioSelectScreenClosedAction,
  audioSelectScreenOpenedAction,
  clickCreateAccountAction,
  clickNotificationAction,
  clickUploadButtonAction,
  connectedToSoundcloudAction,
  connectedToTuneCoreAction,
  connectToSoundcloudAttemptAction,
  connectToTuneCoreAttemptAction,
  createQrAction,
  initiateV2VerificationAction,
  openIntercomAction,
  openProfileCardAction,
  playlistHeaderImageClickedAction,
  playlistPlayLocation,
  addSongsToFolderAction,
  removeSongsFromFolderAction,
  returnFromV2VerificationAction,
  saveReasonForAccountDeleteAction,
  searchClearedAction,
  searchCollaboratorSelectAction,
  searchSongSelectAction,
  searchStartedAction,
  selectSongAction,
  signUpAccountCreatedAction,
  signUpEmailPasswordAddedAction,
  signUpMarketingOptInAction,
  signUpPersonalDetailsAddedAction,
  signUpResendVerificationAction,
  signUpStartedAction,
  signUpVerifiedAction,
  soundCloudReleaseCreatedAction,
  soundcloudReleaseExportCreatedAction,
  subscriptionCancelledAction,
  subscriptionCreatedAction,
  subscriptionManageButtonAction,
  subscriptionSubscribeButtonAction,
  subscriptionTierSelectedAction,
  subscriptionTierSelectionCancelledAction,
  tuneCoreAudioSelectScreenOpenedAction,
  tuneCoreReleaseCreatedAction,
  tuneCoreReleaseExportCreatedAction,
  previewSharedAudioAction,
  previewSharedPlaylistAction,
  openSharedLyricsAction,
  openSharedCreditsAction,
  audioShareLyricsBlockedAction,
  audioShareCreditsBlockedAction,
  openSharedContactCardAction,
  labelV2ReleaseCreatedAction,
  copySuggestionAction,
} from '../../Mixpanel/slice';

const NON_TRACKING_EMAILS = 'mailautomation.test.session.id';

const projectToken = isProd()
  ? process.env.REACT_APP_MIXPANEL_PROD_PROJECT_TOKEN
  : process.env.REACT_APP_MIXPANEL_TEST_PROJECT_TOKEN;

const completedSuffix = '/fulfilled';
const pendingSuffix = '/pending';

export function mixpanelMiddleware(state: any) {
  mixpanel.init(projectToken as string);

  const searchParams = new URLSearchParams(window.location.search);
  const shouldOptOut =
    searchParams.get('automated') || config.env === 'development';

  if (shouldOptOut) {
    mixpanel.opt_out_tracking();
  }

  return (next: Dispatch) => (action: AnyAction) => {
    const result = next(action);

    switch (action.type) {
      case 'auth/logout': {
        mixpanel.track('Logout');
        mixpanel.reset();
        mixpanel.clear_opt_in_out_tracking();
        break;
      }

      case `${signInEffect.typePrefix}${completedSuffix}`: {
        const {
          auth: {
            account: { accountEmail },
          },
        } = state.getState();

        if (accountEmail.includes(NON_TRACKING_EMAILS) || shouldOptOut) {
          mixpanel.opt_out_tracking();
        }

        mixpanel.track('Sign in click');

        break;
      }

      case clickCreateAccountAction.type: {
        const {
          mixpanel: { referralOrigin, referral },
          shares: { shareId },
        } = state.getState();

        mixpanel.track('Create account click', {
          'Referral ID': referral,
          Origin: referralOrigin,
          ...(shareId ? { shareId } : {}),
        });
        break;
      }

      case signUpStartedAction.type: {
        const { payload: Origin } = action;
        mixpanel.track('Sign up started', {
          Origin,
        });
        break;
      }

      case signUpEmailPasswordAddedAction.type: {
        const {
          mixpanel: { referralOrigin, referral },
        } = state.getState();

        mixpanel.track('Sign up email password added', {
          'Referral ID': referral,
          Origin: referralOrigin,
          $current_url: removePersonalDataFromUrl(window.location.href),
        });
        break;
      }

      case signUpPersonalDetailsAddedAction.type: {
        const {
          mixpanel: { referralOrigin, referral },
        } = state.getState();

        mixpanel.track('Sign up personal details added', {
          'Referral ID': referral,
          Origin: referralOrigin,
          $current_url: removePersonalDataFromUrl(window.location.href),
        });
        break;
      }

      case signUpMarketingOptInAction.type: {
        const {
          mixpanel: { referralOrigin, referral },
        } = state.getState();

        mixpanel.track('Sign up marketing opt in', {
          'Referral ID': referral,
          Origin: referralOrigin,
          $current_url: removePersonalDataFromUrl(window.location.href),
        });
        break;
      }

      case signUpAccountCreatedAction.type: {
        const {
          mixpanel: { referralOrigin, referral },
        } = state.getState();
        const current_url = removePersonalDataFromUrl(window.location.href);

        mixpanel.track(
          'Sign up role selected',
          {
            Role: action.payload.role,
            'Referral ID': referral,
            Origin: referralOrigin,
            $current_url: current_url,
          },
          () => {
            mixpanel.track('Sign up account created', {
              'Referral ID': referral,
              Origin: referralOrigin,
              $current_url: current_url,
            });
          }
        );
        break;
      }

      case signUpVerifiedAction.type: {
        const {
          mixpanel: { referralOrigin, referral },
        } = state.getState();

        mixpanel.track('Sign up verified', {
          'Referral ID': referral,
          Origin: referralOrigin,
          $current_url: removePersonalDataFromUrl(window.location.href),
        });
        break;
      }

      case signUpResendVerificationAction.type: {
        const {
          mixpanel: { referralOrigin, referral },
        } = state.getState();

        mixpanel.track('Sign up resend verification', {
          'Referral ID': referral,
          Origin: referralOrigin,
          $current_url: removePersonalDataFromUrl(window.location.href),
        });
        break;
      }

      case selectSongAction.type: {
        mixpanel.track('Selected song', { 'Song Guid': action.payload });
        break;
      }

      case `${fetchBootstrapEffect.typePrefix}${completedSuffix}`: {
        const userGuid = action.payload?.account?.userGuid;
        if (userGuid) {
          mixpanel.identify(userGuid);
          mixpanel.register({
            Platform: config.isElectronBuild ? 'electron' : 'web',
            'App Version': config.appVersion,
          });
        }
        break;
      }

      case `${handleCreateSongWithDeeplinkingEffect.typePrefix}${completedSuffix}`: {
        if (action.meta?.arg?.isFromProTools) {
          mixpanel.track('Create badge song', { 'Song Guid': action.payload });
        } else {
          mixpanel.track('Create song', { 'Song Guid': action.payload });
        }
        break;
      }

      case `${uploadAudioEffect.typePrefix}${completedSuffix}`: {
        const {
          meta: { arg },
        } = action;
        const { type, isPrivate, guid } = arg.audio as Audio;
        mixpanel.track('Uploading audio', {
          'Audio Type': type,
          'Privacy Setting': isPrivate,
          'Audio Guid': guid,
        });
        break;
      }

      case fetchAudioForPlayingEffect.fulfilled.type: {
        const { payload, meta } = action as ReturnType<
          typeof fetchAudioForPlayingEffect.fulfilled
        >;

        mixpanel.track('Played song', {
          'Song Guid': payload?.parentGuid,
          'Audio Guid': payload?.audioGuid,
          'Playlist Audio Guid': meta.arg.playlistAudioGuid,
        });
        break;
      }

      case `${createSharedLinkEffect.typePrefix}${completedSuffix}`: {
        const { payload } = action as ReturnType<
          typeof createSharedLinkEffect.fulfilled
        >;
        mixpanel.track('Created share link', {
          'Song ID': payload.songGuid,
        });
        break;
      }

      case `${previewSharedAudioAction.type}`: {
        const { payload } = action as ReturnType<
          typeof previewSharedAudioAction
        >;
        mixpanel.track('Audio share link previewed', {
          'Song ID': payload.songGuid,
          'Credits Included': payload.creditsShared,
          'Lyrics Included': payload.lyricsShared,
          'Download Included': payload.downloadable,
        });
        break;
      }

      case `${previewSharedPlaylistAction.type}`: {
        const { payload } = action as ReturnType<
          typeof previewSharedPlaylistAction
        >;

        mixpanel.track('Playlist share link previewed', {
          'Playlist ID': payload.playlistGuid,
          'Sharelink Count': payload.shareCount,
          'Credits Included': payload.creditsShared,
          'Lyrics Included': payload.lyricsShared,
          'Download Included': payload.downloadable,
        });
        break;
      }

      case `${openSharedCreditsAction.type}`: {
        const { payload } = action as ReturnType<
          typeof openSharedCreditsAction
        >;
        mixpanel.track('Share link credits viewed', {
          ...(payload.playlistGuid
            ? { 'Playlist ID': payload.playlistGuid }
            : {}),
          ...(payload.songGuid ? { 'Song ID': payload.songGuid } : {}),
        });
        break;
      }

      case `${openSharedContactCardAction.type}`: {
        const { payload } = action as ReturnType<
          typeof openSharedContactCardAction
        >;
        mixpanel.track('Share link contact card viewed', {
          ...(payload.playlistGuid
            ? { 'Playlist ID': payload.playlistGuid }
            : {}),
          ...(payload.songGuid ? { 'Song ID': payload.songGuid } : {}),
        });
        break;
      }

      case `${openSharedLyricsAction.type}`: {
        const { payload } = action as ReturnType<typeof openSharedLyricsAction>;
        mixpanel.track('Share link lyrics viewed', {
          ...(payload.playlistGuid
            ? { 'Playlist ID': payload.playlistGuid }
            : {}),
          ...(payload.songGuid ? { 'Song ID': payload.songGuid } : {}),
        });
        break;
      }

      case `${audioShareLyricsBlockedAction.type}`: {
        mixpanel.track('Audio share lyrics blocked');
        break;
      }

      case `${audioShareCreditsBlockedAction.type}`: {
        mixpanel.track('Audio share credits blocked');
        break;
      }

      case `${setSongLatestAudioVersionEffect.typePrefix}${completedSuffix}`: {
        mixpanel.track('Marked as latest');
        break;
      }

      case `${downloadAudioEffect.typePrefix}${completedSuffix}`: {
        mixpanel.track('Downloaded file');
        break;
      }

      case `${deleteSongAudioEffect.typePrefix}${completedSuffix}`: {
        mixpanel.track('Deleted file');
        break;
      }

      case lyricsEditAction.type: {
        const { meta } = action as ReturnType<typeof lyricsEditAction>;
        mixpanel.track('Lyrics edit', {
          lyricsSheetEmpty: meta.isLyricsSheetEmpty,
        });
        break;
      }

      case `${downloadLyricsPdfEffect.typePrefix}${completedSuffix}`: {
        mixpanel.track('Lyrics download');
        break;
      }

      case `${createContributionEffect.typePrefix}${completedSuffix}`: {
        const {
          payload: {
            user,
            className,
            createdBy: { userGuid },
          },
        } = action;

        if (!user) {
          mixpanel.track('Credited by name');
          break;
        }

        if (user.userGuid !== userGuid) {
          mixpanel.track('Credited user', { 'Class Name': className });
          break;
        }
        mixpanel.track('Credited himself', { 'Class Name': className });
        mixpanel.people.set('Class Name', className);
        break;
      }

      case updateCollaboratorSearchAction.type: {
        mixpanel.track('Search profile');
        break;
      }

      case `${inviteCollaboratorsEffect.typePrefix}${completedSuffix}`: {
        const { inviteeUserGuids, inviteeEmails } = action.meta.arg.payload;

        inviteeUserGuids?.forEach(({ guid, inviteType }) => {
          mixpanel.track('Invited collaborator', {
            'Invited User ID': guid,
            'Invite Type': inviteType,
          });
        });
        inviteeEmails?.forEach(() => {
          mixpanel.track('Invited new user');
        });
        break;
      }

      case `${createSplitEffect.typePrefix}${completedSuffix}`: {
        const { payload } = action;
        const { songGuid } = payload as LatestSplit;
        mixpanel.track('Created split');
        mixpanel.track('Accepted Split', { 'Song Guid': songGuid });
        if (isSplitAcceptedByAll(payload)) {
          mixpanel.track('Accepted by all', { 'Song Guid': songGuid });
        }
        break;
      }

      case `${acceptSplitEffect.typePrefix}${completedSuffix}`: {
        const { payload } = action;
        const { songGuid } = payload as LatestSplit;
        mixpanel.track('Accepted Split', { 'Song Guid': songGuid });
        if (isSplitAcceptedByAll(payload)) {
          mixpanel.track('Accepted by all', { 'Song Guid': songGuid });
        }
        break;
      }

      case `${createReleaseEffect.typePrefix}${completedSuffix}`: {
        const {
          meta: {
            arg: { type },
          },
        } = action;
        if (type !== 'SoundCloud' && type !== 'TuneCore') {
          mixpanel.track('Created release');
        }
        break;
      }

      case `${exportReleaseEffect.typePrefix}${completedSuffix}`: {
        mixpanel.track('Export release');
        break;
      }

      case `${addIpiManuallyEffect.typePrefix}${completedSuffix}`: {
        const {
          meta: {
            arg: { societyTitle },
          },
        } = action;
        mixpanel.track('Added IPI', { 'Society Name': societyTitle });
        break;
      }

      case `${addIpnManuallyEffect.typePrefix}${completedSuffix}`: {
        const {
          meta: {
            arg: { societyTitle },
          },
        } = action;
        mixpanel.track('Added IPN', { 'Society Name': societyTitle });
        break;
      }

      case `${uploadProfileImageEffect.typePrefix}${completedSuffix}`: {
        mixpanel.track('Changed avatar');
        break;
      }

      case `${verifyEmailEffect.typePrefix}${completedSuffix}`: {
        mixpanel.track('Changed email');
        break;
      }

      case openIntercomAction.type: {
        mixpanel.track('Opened intercom');
        break;
      }

      case `${fetchNotificationsEffect.typePrefix}${completedSuffix}`: {
        mixpanel.track('Opened Notifications');
        break;
      }

      case updateSongListSearchAction.type: {
        mixpanel.track('Song search');
        break;
      }
      case saveReasonForAccountDeleteAction.type: {
        const { payload } = action;

        mixpanel.track('Delete account', {
          reason: payload || '',
        });
        break;
      }

      case `${updateProfileEffect.typePrefix}${completedSuffix}`: {
        const {
          payload,
          meta: { arg },
        } = action;

        if (shouldSendDefaultIdEvent(arg.formData)) {
          mixpanel.track('Default ID set');
        }

        const updatedIds = getUpdatedIdsUserProperty(payload, arg.formData);
        if (updatedIds) {
          mixpanel.people.set('Default ID', updatedIds);
        }
        break;
      }

      case clickNotificationAction.type: {
        const { payload } = action;
        mixpanel.track('Clicked notification', {
          Notification: payload,
        });
        break;
      }

      case `${updateSongEffect.typePrefix}${completedSuffix}`: {
        const {
          meta: { arg },
          payload,
        } = action;
        const { status, title, ...rest } = arg.formData as Partial<
          SongFormData & SongDetailsFormData
        >;

        if (status) {
          mixpanel.track('Song status change', {
            Status: SongStatusLabel[status],
            'Song Guid': payload.guid,
          });
          break;
        }

        if (title) {
          mixpanel.track('Song title edit', {
            'Song Guid': payload.guid,
          });
          break;
        }

        if (rest) {
          const keys = objectDeepKeys(rest);
          const lastKey = keys[keys.length - 1];
          mixpanel.track('Song details edit', {
            Field: lastKey,
            'Song Guid': payload.guid,
          });
        }

        break;
      }

      case `${updateLabelVersionEffect.typePrefix}${completedSuffix}`: {
        const { meta, payload } = action as ReturnType<
          typeof updateLabelVersionEffect.fulfilled
        >;
        const { formData } = meta.arg;
        const keys = objectDeepKeys(formData);
        const lastKey = keys[keys.length - 1];
        mixpanel.track('Release song details edit', {
          Field: lastKey,
          'Version Guid': payload.versionGuid,
          'Song Guid': payload.songGuid,
        });
        break;
      }

      case clickUploadButtonAction.type: {
        mixpanel.track('Upload audio button click', { Type: 'Audio' });
        break;
      }

      case `${respondToInviteEffect.typePrefix}${completedSuffix}`: {
        const {
          meta: { arg },
        } = action;
        const { inviterGuid, response } = arg as RespondToInviteEffectData;

        mixpanel.track(
          response === 'ACCEPT'
            ? 'Accepted collaborator invite'
            : 'Declined collaborator invite',
          {
            'Inviter User ID': inviterGuid,
          }
        );
        break;
      }

      case `${createIsniEffect.typePrefix}${completedSuffix}`: {
        mixpanel.track('Added ISNI');
        break;
      }

      case initiateV2VerificationAction.type: {
        const { payload } = action;
        mixpanel.track('V2 ID verification initiate', {
          'Society Name': payload,
        });
        break;
      }

      case returnFromV2VerificationAction.type: {
        mixpanel.track('V2 ID verification return');
        break;
      }

      case setSharedAudio.type: {
        const { payload } = action as ReturnType<typeof setSharedAudio>;
        mixpanel.track('Audio share link open', {
          'Creator Display Name':
            payload?.response?.share?.item?.createdBy?.profileCard
              ?.displayName || '',
        });
        break;
      }

      case createQrAction.type: {
        mixpanel.track('Created QR code');
        break;
      }

      case `${updateSongAudioEffect.typePrefix}${completedSuffix}`: {
        const { payload } = action;
        const { type, isPrivate } = payload as Audio;
        mixpanel.track('Audio edit', {
          'Audio Type': type,
          'Privacy Setting': isPrivate,
        });
        break;
      }

      /// SOUNDCLOUD events
      case connectToSoundcloudAttemptAction.type: {
        mixpanel.track('Soundcloud connect attempt');
        break;
      }
      case connectedToSoundcloudAction.type: {
        mixpanel.track('Soundcloud connected');
        break;
      }
      case soundCloudReleaseCreatedAction.type: {
        const { payload } = action;
        const {
          mixpanel: { selectedAudioCount },
        } = state.getState();
        mixpanel.track('Soundcloud release created', {
          ...payload,
          audioCount: selectedAudioCount,
        });
        break;
      }
      case soundcloudReleaseExportCreatedAction.type: {
        const { payload } = action;
        mixpanel.track('Soundcloud release exported', payload);
        break;
      }
      case audioSelectScreenOpenedAction.type: {
        mixpanel.track('Soundcloud audio selection opened');
        break;
      }
      // SOUNDCLOUD events end
      // TUNECORE events
      case connectToTuneCoreAttemptAction.type: {
        const { payload } = action;
        mixpanel.track('TuneCore connect attempt', payload);
        break;
      }
      case connectedToTuneCoreAction.type: {
        mixpanel.track('TuneCore connected');
        break;
      }
      case tuneCoreReleaseCreatedAction.type: {
        const { payload } = action;
        const {
          mixpanel: { selectedAudioCount },
        } = state.getState();
        mixpanel.track('TuneCore release created', {
          ...payload,
          audioCount: selectedAudioCount,
        });
        break;
      }
      case labelV2ReleaseCreatedAction.type: {
        const { payload } = action;
        const {
          mixpanel: { selectedAudioCount },
        } = state.getState();
        mixpanel.track('Connex release created', {
          ...payload,
          audioCount: selectedAudioCount,
        });
        break;
      }
      case `${attachAudioToReleaseEffect.typePrefix}${completedSuffix}`: {
        const {
          releases: { release },
        } = state.getState();
        if (release?.type !== 'TuneCore') {
          break;
        }
        const alreadyHadVersions =
          release.versions.filter((version) => !version.isNew).length > 0;
        if (alreadyHadVersions) {
          mixpanel.track('TuneCore audio added');
        }
        break;
      }
      case tuneCoreReleaseExportCreatedAction.type: {
        const { payload } = action;
        const {
          releases: { releaseVersionsDetails },
        } = state.getState();
        const versionsLyrics = releaseVersionsDetails.map((item) => ({
          lyricsExcluded: item?.lyrics?.isExcluded || false,
        }));

        mixpanel.track('TuneCore release exported', {
          ...payload,
          type: payload?.audioCount > 1 ? 'Album' : 'Single',
          versionsLyrics,
        });
        break;
      }
      case tuneCoreAudioSelectScreenOpenedAction.type: {
        mixpanel.track('TuneCore audio selection opened');
        break;
      }
      case `${updateReleaseVersionEffect.typePrefix}${completedSuffix}`: {
        const { meta } = action as ReturnType<
          typeof updateReleaseVersionEffect.fulfilled
        >;
        const { releaseType, formData } = meta.arg;
        if (
          releaseType === 'TuneCore' &&
          (formData as TuneCoreVersionDetailsFormData).versionTitle
        ) {
          mixpanel.track('TuneCore track title change');
        }
        break;
      }
      // TUNECORE events end
      // Mixed providers events
      case `${syncVersionEffect.typePrefix}${pendingSuffix}`: {
        const {
          releases: { release },
        } = state.getState();

        if (release?.type === 'TuneCore') {
          mixpanel.track('TuneCore version synced');
          break;
        }

        mixpanel.track('Sync button click');
        break;
      }

      case `${deleteConnectionEffect.typePrefix}${completedSuffix}`: {
        const { meta } = action as ReturnType<
          typeof deleteConnectionEffect.fulfilled
        >;
        if (meta.arg.providerId === 'SoundCloud') {
          mixpanel.track('Soundcloud disconnected');
        }
        if (meta.arg.providerId === 'TuneCore') {
          mixpanel.track('TuneCore disconnected');
        }
        break;
      }
      case `${replaceVersionAudioEffect.typePrefix}${completedSuffix}`: {
        const {
          releases: { release },
        } = state.getState();

        if (release?.type === 'SoundCloud') {
          mixpanel.track('Soundcloud audio changed');
        }
        if (release?.type === 'TuneCore') {
          mixpanel.track('TuneCore audio changed');
        }
        break;
      }
      case audioSelectScreenClosedAction.type: {
        const {
          payload: { releaseType },
        } = action;
        const {
          mixpanel: { selectedAudioCount },
        } = state.getState();
        const type = releaseType[0].toUpperCase() + releaseType.slice(1);

        if (type === 'SoundCloud') {
          mixpanel.track('Soundcloud audio selection cancelled', {
            audioCount: selectedAudioCount,
          });
        }

        if (type === 'TuneCore') {
          mixpanel.track('TuneCore audio selection cancelled', {
            audioCount: selectedAudioCount,
          });
        }
        break;
      }
      // Mixed providers events end
      case subscriptionSubscribeButtonAction.type: {
        mixpanel.track('Clicked subscribe');
        break;
      }
      case subscriptionManageButtonAction.type: {
        mixpanel.track('Clicked manage subscription');
        break;
      }
      case subscriptionTierSelectedAction.type: {
        const { payload } = action;
        mixpanel.track('Subscription tier selected', payload);
        break;
      }
      case subscriptionTierSelectionCancelledAction.type: {
        mixpanel.track('Tier selection cancelled');
        break;
      }
      case subscriptionCreatedAction.type: {
        mixpanel.track('Subscription purchased');
        break;
      }
      case subscriptionCancelledAction.type: {
        mixpanel.track('Checkout cancelled');
        break;
      }
      case uploadAudioBlockAction.type: {
        const appState = state.getState();
        const subscription = getSubscription(appState);
        const tierListDDL = getSubscriptionTiersDDL(appState);
        const storage = getStorage(appState);
        const tierLabel = getCurrentTierLabel(subscription, tierListDDL);
        const totalGB = bytesToGigabytes(storage?.limitBytes || 0);
        const limit = `${totalGB} GB`;
        mixpanel.track('Audio upload blocked', {
          Limit: limit,
          Tier: tierLabel,
        });
        break;
      }
      case searchStartedAction.type: {
        const { payload: pageTitle } = action;
        mixpanel.track('Song search start', { pageTitle });
        break;
      }
      case searchClearedAction.type: {
        mixpanel.track('Song search clear');
        break;
      }
      case searchSongSelectAction.type: {
        const { payload: songGuid } = action;
        const appState = state.getState();
        const searchByCollaborator = getGlobalSearchByCollaborator(appState);

        if (searchByCollaborator) {
          mixpanel.track('Collaborator song select', { songId: songGuid });
        } else {
          mixpanel.track('Song search select', { songId: songGuid });
        }
        break;
      }
      case searchCollaboratorSelectAction.type: {
        const { payload: collaboratorGuid } = action;
        mixpanel.track('Song search collaborator select', {
          collaboratorUserId: collaboratorGuid,
        });
        break;
      }

      case createPlaylistEffect.fulfilled.type: {
        const { payload, meta } = action as ReturnType<
          typeof createPlaylistEffect.fulfilled
        >;
        const { audioCount, songGuid } = meta.arg;
        mixpanel.track('Created Playlist', {
          'Playlist ID': payload.guid,
          'Audio Count': audioCount,
          'Song ID': songGuid,
        });
        break;
      }

      case addPlaylistAudiosEffect.fulfilled.type: {
        const { payload, meta } = action as ReturnType<
          typeof addPlaylistAudiosEffect.fulfilled
        >;
        const {
          context,
          playlistGuid,
          songGuid,
          withPlaylistCreate,
        } = meta.arg;
        const location = getAudioAddedLocation(
          context || songGuid ? 'SONG_ROOM' : undefined
        );
        if (!location || withPlaylistCreate) {
          break;
        }
        mixpanel.track('Playlist Audio added', {
          'Playlist ID': playlistGuid,
          Location: location,
          'Audio Count': payload.length,
          'Song ID': songGuid,
        });
        break;
      }

      case deletePlaylistAudioEffect.fulfilled.type: {
        const { meta } = action as ReturnType<
          typeof deletePlaylistAudioEffect.fulfilled
        >;
        const playlist = getPlaylist(state.getState());
        mixpanel.track('Playlist Audio deleted', {
          'Playlist ID': playlist?.guid,
          'Audio ID': meta.arg.audioGuid,
        });
        break;
      }

      case createSharedPlaylistLinkEffect.fulfilled.type: {
        const { meta } = action as ReturnType<
          typeof createSharedPlaylistLinkEffect.fulfilled
        >;
        const sharesCount = getSharedPlaylistLinks(
          state.getState(),
          meta.arg.playlistGuid
        ).length;
        mixpanel.track('Playlist Shared', {
          'Playlist ID': meta.arg.playlistGuid,
          'Share Link Count': sharesCount,
        });
        break;
      }

      case revokeSharedPlaylistLinkEffect.fulfilled.type: {
        const { meta } = action as ReturnType<
          typeof revokeSharedPlaylistLinkEffect.fulfilled
        >;
        const revokedCount = getSharedPlaylistLinks(
          state.getState(),
          meta.arg.guid
        ).filter((el) => el.status === 'REVOKED').length;
        mixpanel.track('Playlist link revoked', {
          'Playlist ID': meta.arg.guid,
          'Revoked Link Count': revokedCount,
        });
        break;
      }

      case playlistPlayLocation.type: {
        const { payload } = action as ReturnType<typeof playlistPlayLocation>;
        mixpanel.track('Playlist audio plays', {
          'Playlist ID': payload.playlistGuid,
          Location: getPlaylistPlayedLocation(payload.location),
        });
        break;
      }

      case setShowUpgradePopup.type: {
        const { payload } = action as ReturnType<typeof setShowUpgradePopup>;
        const { playlistGuid, audioGuid } = payload?.context || {};
        const eventName = getPlaylistUpgradePopupEventName(payload);

        if (eventName) {
          mixpanel.track(eventName, {
            ...(playlistGuid ? { 'Playlist ID': playlistGuid } : {}),
            ...(audioGuid ? { 'Audio ID': audioGuid } : {}),
          });
        }
        break;
      }

      case updateSharedPlaylistLinkEffect.fulfilled: {
        const {
          meta: {
            arg: { data, guid },
          },
        } = action as ReturnType<
          typeof updateSharedPlaylistLinkEffect.fulfilled
        >;
        if (data.downloadable !== undefined) {
          mixpanel.track('Playlist link download', {
            'Playlist ID': guid,
            'Download On': data.downloadable,
          });
        } else if (data.creditsShared !== undefined) {
          mixpanel.track('Playlist link credits', {
            'Playlist ID': guid,
            'Credits On': data.creditsShared,
          });
        } else if (data.lyricsShared !== undefined) {
          mixpanel.track('Playlist link lyrics', {
            'Playlist ID': guid,
            'Lyrics On': data.lyricsShared,
          });
        }
        break;
      }

      case playlistHeaderImageClickedAction.type: {
        const { payload } = action as ReturnType<
          typeof playlistHeaderImageClickedAction
        >;
        mixpanel.track('Playlist cover image pressed', {
          'Playlist ID': payload,
        });
        break;
      }

      case uploadPlaylistImageEffect.fulfilled.type: {
        const playlist = getPlaylist(state.getState());
        mixpanel.track('Playlist cover image created', {
          'Playlist ID': playlist?.guid,
        });
        break;
      }

      case createChatMessageEffect.fulfilled.type: {
        const { meta } = action as ReturnType<
          typeof createChatMessageEffect.fulfilled
        >;
        const appState = state.getState();
        const currentChannelMessages = getCurrentChannelMessages(appState);
        const currentChannel = getCurrentChannel(appState);

        if (currentChannelMessages.length <= 1) {
          mixpanel.track('Chat Initiated', {
            'Song ID': currentChannel?.parentGuid,
            'Channel ID': meta.arg.channelGuid,
          });
        }

        if (meta.arg.isFirstMessage) {
          mixpanel.track('Chat Message Sent', {
            'Song ID': currentChannel?.parentGuid,
            'Channel ID': meta.arg.channelGuid,
          });
        }

        break;
      }

      case openProfileCardAction.type: {
        const { payload } = action as ReturnType<typeof openProfileCardAction>;
        mixpanel.track('Clicked Contact Card', {
          'Song ID': payload,
          'USER ID': getUserGuid(),
        });
        break;
      }

      case addSongsToFolderAction.type: {
        const {
          payload: { folderId, songsAdded },
        } = action as ReturnType<typeof addSongsToFolderAction>;

        mixpanel.track('Songs added to folder', {
          'Folder ID': folderId,
          'Songs Added': songsAdded,
        });
        break;
      }

      case removeSongsFromFolderAction.type: {
        const {
          payload: { folderId, songsRemoved },
        } = action as ReturnType<typeof removeSongsFromFolderAction>;

        mixpanel.track('Songs removed from folder', {
          'Folder ID': folderId,
          'Songs Removed': songsRemoved,
        });
        break;
      }

      case pinSongEffect.fulfilled.type: {
        const {
          meta: {
            arg: { guid },
          },
        } = action as ReturnType<typeof pinSongEffect.fulfilled>;

        mixpanel.track('Song pinned', {
          'Song ID': guid,
        });
        break;
      }

      case unpinSongEffect.fulfilled.type: {
        const {
          meta: {
            arg: { guid },
          },
        } = action as ReturnType<typeof unpinSongEffect.fulfilled>;

        mixpanel.track('Song unpinned', {
          'Song ID': guid,
        });
        break;
      }

      case createFolderEffect.fulfilled.type: {
        mixpanel.track('Folder created');
        break;
      }

      case deleteFolderEffect.fulfilled.type: {
        const { payload } = action as ReturnType<
          typeof deleteFolderEffect.fulfilled
        >;

        mixpanel.track('Folder deleted', {
          'Total Songs': payload?.songCount || 0,
        });
        break;
      }

      case setCoWriterBarSettings.type: {
        if (action.payload) {
          const { type } = action.payload;
          mixpanel.track('Lyrics Inspiration Invoke', {
            type,
          });
        }
        break;
      }

      case fetchLyricsInspirationSearchEffect.fulfilled.type: {
        const {
          meta: {
            arg: { type },
          },
        } = action as ReturnType<
          typeof fetchLyricsInspirationSearchEffect.fulfilled
        >;
        mixpanel.track('Lyrics Inspiration Search', {
          type,
        });
        break;
      }

      case copySuggestionAction.type: {
        mixpanel.track('Lyrics Inspiration Copy');
        break;
      }

      default:
        break;
    }

    return result;
  };
}
